import * as THREE from 'three';
import { Engine, detectQuality, isTouchDevice } from './core/Engine.js';
import { TouchInput } from './core/TouchInput.js';
import { Events } from './core/Events.js';
import { Input } from './core/Input.js';
import { AssetForge } from './core/AssetForge.js';
import { makeRng } from './core/Rng.js';
import { PhysicsWorld } from './world/Physics.js';
import { Player } from './player/Player.js';
import { CameraRig } from './player/CameraRig.js';

import { Sky } from './render/Sky.js';
import { Lighting } from './render/Lighting.js';
import { PostFX } from './render/PostFX.js';
import { Level } from './world/Level.js';
import { WeaponSystem } from './weapons/WeaponSystem.js';
import { Melee } from './weapons/Melee.js';
import { Equipment } from './game/Equipment.js';
import { DroppedWeapons } from './weapons/DroppedWeapons.js';
import { AIDirector } from './ai/AIDirector.js';
import { FXSystem } from './fx/FXSystem.js';
import { AudioEngine } from './audio/AudioEngine.js';
import { HUD } from './ui/HUD.js';
import { TeamMarkers } from './ui/TeamMarkers.js';
import { Objectives } from './ui/Objectives.js';
import { Banner } from './ui/Banner.js';
import { Multiplayer } from './ui/Multiplayer.js';
import { NetSession } from './net/NetSession.js';
import { ModeSelect, savedSkill } from './ui/ModeSelect.js';
import { GameDirector } from './game/GameDirector.js';
import { MatchAuthority } from './game/MatchAuthority.js';
import { createRules } from './game/rules/index.js';
import { bootstrapProgression } from './ui/Progression.js';
import { setExpansionCondition } from './weapons/attachments/WeaponLoadouts.js';

const boot = document.getElementById('boot');
const bootStatus = boot.querySelector('.boot-status');
const bootBar = boot.querySelector('.boot-bar i');

/**
 * Which mode this match runs. A URL parameter beats the saved choice, so a link
 * can put a friend straight into the same mode without them going through a menu.
 */
function pickMode() {
  const q = new URLSearchParams(location.search).get('mode');
  if (q) return q;
  try {
    return localStorage.getItem('ob.mode') ?? 'skirmish';
  } catch {
    return 'skirmish';
  }
}

/** Bots per side, from the difficulty screen. Ignored by modes without teams. */
function botSquad() {
  try {
    const n = Number(localStorage.getItem('ob.squad'));
    return Number.isFinite(n) && n >= 2 && n <= 8 ? n : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Yields to the browser so the boot screen can actually paint between steps.
 *
 * rAF is the correct signal when the tab is compositing, but a backgrounded or
 * throttled tab never fires it — and boot would wedge forever at 8%. Race it
 * against a timer so loading can never depend on a frame that may not come.
 */
const step = (label, pct) =>
  new Promise((resolve) => {
    bootStatus.textContent = label;
    bootBar.style.width = `${pct}%`;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(finish, 60);
    requestAnimationFrame(() => requestAnimationFrame(finish));
  });

async function main() {
  const canvas = document.getElementById('game');
  const quality = detectQuality();

  await step('BOOTING RENDERER', 8);
  const engine = new Engine(canvas, quality);
  const events = new Events();
  const input = new Input(canvas, events);
  const rng = makeRng(0xc0ffee);
  const physics = new PhysicsWorld();

  await step('FORGING MATERIALS', 20);
  const forge = new AssetForge(engine.renderer, quality.level);

  /** @type {import('./types.js').GameContext} */
  const ctx = {
    scene: engine.scene,
    camera: engine.camera,
    viewScene: engine.viewScene,
    viewCamera: engine.viewCamera,
    renderer: engine.renderer,
    engine,
    input,
    events,
    forge,
    physics,
    rng,
    quality,
  };
  window.__ctx = ctx; // debug handle; harmless in production and invaluable in dev

  await step('RAISING SKY', 32);
  ctx.sky = new Sky(ctx);

  await step('PLACING LIGHTS', 42);
  ctx.lighting = new Lighting(ctx);

  await step('BUILDING LEVEL', 58);
  ctx.level = new Level(ctx);
  ctx.level.build();

  await step('ARMING', 72);
  /**
   * Before anything that can be hurt, because everything that can be hurt enrols
   * with it in its own constructor. This is the module that decides who damaged
   * whom and who is dead — nothing else is allowed to — and the one whose `rules`
   * a game mode replaces.
   *
   * The mode is chosen here, before the first soldier exists, because the roster it
   * asks for is what the AI director spawns. Switching mid-match would mean moving
   * every bot to a different side, so the pre-match screen is the only place it can
   * be set.
   */
  ctx.match = new MatchAuthority(ctx, createRules(pickMode(), { squad: botSquad() }));

  const spawn = ctx.level.playerSpawn ?? new THREE.Vector3(0, 2, 0);
  ctx.player = new Player(ctx, spawn);
  ctx.cameraRig = new CameraRig(ctx, ctx.player);
  ctx.weapons = new WeaponSystem(ctx, ctx.player);
  // After the weapon system, so its 'sim' callback runs after the behaviour modules
  // that clear `fireInhibit` at the top of every frame. Melee holds that same flag
  // for the length of a swing; if it ran first, the clear would drop it.
  ctx.melee = new Melee(ctx, ctx.player);
  ctx.equipment = new Equipment(ctx, ctx.player);

  await step('SPAWNING HOSTILES', 84);
  ctx.ai = new AIDirector(ctx);

  await step('WARMING EFFECTS', 92);
  ctx.dropped = new DroppedWeapons(ctx);
  ctx.fx = new FXSystem(ctx);
  ctx.audio = new AudioEngine(ctx);
  ctx.hud = new HUD(ctx);
  ctx.teamMarkers = new TeamMarkers(ctx);
  ctx.objectives = new Objectives(ctx);
  ctx.banner = new Banner(ctx);
  ctx.modeSelect = new ModeSelect(ctx);
  /**
   * Multiplayer. Constructed always and connected never, until a player asks: an
   * idle session costs one update callback that returns on its first line, and
   * building it lazily would mean `ctx.net` being undefined in the one place that
   * has to check it — `MatchAuthority.requestDamage`, on every single shot.
   */
  ctx.net = new NetSession(ctx);
  ctx.multiplayer = new Multiplayer(ctx);
  // Bot skill is four multipliers read through a getter, so applying it after the
  // director exists reaches every soldier already standing on the map.
  ctx.ai.setSkill(savedSkill());

  await step('COMPILING SHADERS', 97);
  const postfx = new PostFX(ctx);
  ctx.postfx = postfx;
  engine.composer = postfx.composer;

  // Cross-system wiring lives here, after every module exists.
  ctx.director = new GameDirector(ctx);

  // Career progression: persistent level/XP and the loadout screen. Deliberately
  // separate from the director's scorestreak score — that one resets on death,
  // this one survives the match. Sets ctx.progression.
  bootstrapProgression(ctx);

  /**
   * Who earns the expanded 8-point build limit.
   *
   * Deliberately *not* "has a mastery fitted", which was the obvious candidate: a
   * mastery costs 2 points, so granting 3 more for wearing one makes it a net gain
   * and turns every conversion back into the strict upgrade the catalog was built
   * to avoid. There is no perk or wildcard system here to spend a slot on either,
   * so the honest cost is time: it is a late career unlock, earned once and shared
   * by every weapon. Lives here rather than in the attachment module so that module
   * keeps knowing nothing about career progression.
   */
  setExpansionCondition(() => (ctx.progression?.model?.level ?? 1) >= 45);

  // Compile everything before the first frame so we don't hitch on the first shot.
  engine.renderer.compile(engine.scene, engine.camera);
  engine.renderer.compile(engine.viewScene, engine.viewCamera);

  await step('READY', 100);
  boot.classList.add('done');
  setTimeout(() => boot.remove(), 700);

  // Gamepads have no events for axes, so they must be polled — before anything
  // reads input, hence the 'input' phase.
  engine.registerUpdate(() => input.pollGamepad(), 'input');
  engine.registerUpdate(() => input.endFrame(), 'ui');
  engine.start();

  // The start screen says "click or press any key to deploy", and it has to mean
  // it. Deploying used to be a side effect of pointer lock succeeding, so a browser
  // that refuses or defers the lock — which is every browser the user has already
  // declined it in — left the player clicking a dead screen forever. `engage()`
  // dismisses it unconditionally and is idempotent; the lock is then a bonus.
  canvas.addEventListener('click', () => {
    input.engage();
    input.requestLock();
  });

  // On-screen controls, built last so they sit above the HUD and below the menus.
  // Constructed only for touch devices: a permanently-hidden overlay on a desktop
  // would still be a full-screen `pointer-events: auto` element over the canvas.
  if (isTouchDevice()) {
    ctx.touch = new TouchInput(input, document.getElementById('ui-root') ?? document.body);
    document.documentElement.classList.add('is-touch');
  }

  // The menu owns the pause *decision*; the engine owns the pause *effect*.
  events.on('game:state', ({ state }) => {
    engine.paused = state === 'menu' || state === 'won';
    // Every modal screen — pause, loadout, gunsmith — announces itself as 'menu',
    // and every one of them is tapped directly. The thumb controls would otherwise
    // sit on top of them, eating taps meant for the panel underneath.
    ctx.touch?.setVisible(state === 'playing');
  });

  events.emit('game:state', { state: 'playing' });
}

main().catch((err) => {
  console.error(err);
  bootStatus.textContent = `FAILED: ${err.message}`;
  bootStatus.style.color = '#ff5555';
});
